import React, {
	createContext,
	useContext,
	useMemo,
	useState,
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
} from 'react';

import { Loader } from '@calm-web/design-system';

import { useRecaptchaVisibleState } from '@/hooks/store/useAppState';

import { FullScreenLoaderWrapper } from './styles';

export type IsAuthenticatingContextValue = {
	isAuthenticating: boolean;
	setIsAuthenticating: Dispatch<SetStateAction<boolean>>;
};

const IsAuthenticatingContext = createContext<IsAuthenticatingContextValue | undefined>(undefined);

export const useIsAuthenticating = () => {
	const context = useContext(IsAuthenticatingContext);
	if (!context) {
		throw new Error('useIsAuthenticating must be used within an IsAuthenticatingProvider');
	}
	return context;
};

export default function IsAuthenticatingProvider({ children }: PropsWithChildren) {
	const recaptchaVisible = useRecaptchaVisibleState();

	const [isAuthenticating, setIsAuthenticating] = useState(false);

	const value = useMemo<IsAuthenticatingContextValue>(
		() => ({
			isAuthenticating,
			setIsAuthenticating,
		}),
		[isAuthenticating],
	);

	return (
		<IsAuthenticatingContext.Provider value={value}>
			{isAuthenticating && !recaptchaVisible && (
				<FullScreenLoaderWrapper>
					<Loader color="white" />
				</FullScreenLoaderWrapper>
			)}
			{children}
		</IsAuthenticatingContext.Provider>
	);
}
